import type React from "react";
import { toast } from "react-toastify";
import { useSWRConfig } from "swr";
import { StatusHandleButtons } from "~/components/common/statuses/StatusHandleButtons";
import { useDealContext } from "~/context/DealContext";
import { useDealEstimationHistories } from "~/hooks/deals/useDealEstimationHistories";
import { useDealTodos } from "~/hooks/deals/useDealTodos";
import { dealCacheKey } from "~/hooks/useDeals";
import useLoader from "~/hooks/useLoader";
import { useTodoTemplates } from "~/hooks/useTodoTemplates";
import { PARTIALLY_FINISHED } from "~/models/Status";
import {
	countUncreatedByTemplates,
	filterTodoTemplatesByStatus
} from "~/models/TodoTemplate";
import { isSuccess } from "~/utils/api/api_handler";
import { update as updateStatus } from "~/utils/api/DealStatus";
import { updateAccept } from "~/utils/api/deals/DealEstimationHeader";
import { create as createDealEstimationHeader } from "~/utils/api/deals/DealOwnEstimationHeader";

type Props = {
	allowPartiallyFinish?: boolean;
	backwardDisabled: boolean;
	forwardDisabled: boolean;
	nextStatusId: number;
	prevStatusId?: number | null;
};

export const OwnStatusHandleButtons: React.FC<Props> = ({
	nextStatusId,
	prevStatusId = null,
	forwardDisabled,
	backwardDisabled,
	allowPartiallyFinish = false
}: Props) => {
	const { Loader, showLoader, hideLoader } = useLoader();
	const deal = useDealContext();
	const { mutate: globalMutate } = useSWRConfig();
	const todoTemplates = useTodoTemplates().useIndex().data;
	const dealTodos = useDealTodos(deal.id);
	const { data: dealEstimationHistoryContext } =
		useDealEstimationHistories().index(deal.id);
	if (
		dealEstimationHistoryContext === null ||
		todoTemplates === null ||
		dealTodos.data === null
	)
		return null;
	const estimationHeaderId =
		dealEstimationHistoryContext.dealEstimationHeaderId;
	const uncreatedTodoCount = countUncreatedByTemplates(
		dealTodos.data,
		filterTodoTemplatesByStatus(deal, nextStatusId, todoTemplates)
	);
	const handleUpdateStatus = async (statusId: number): Promise<void> => {
		showLoader();
		const result = await updateStatus(deal.id, statusId, null, null);
		if (isSuccess(result)) {
			await globalMutate(dealCacheKey(deal.id));
			hideLoader();
			toast.success(`ステータスを更新しました`);
			return;
		}
		hideLoader();
		toast.error("エラーが発生しました");
	};
	const handleForwardStatus = async (): Promise<void> => {
		const result = estimationHeaderId
			? // すでにdraftの見積りがある場合は、履歴を承認させる
				// draftの見積りが全て消えたら、以下を対応
				// https://itandi-dev.atlassian.net/browse/NSKDEV-1622対応
				await updateAccept(deal.id, estimationHeaderId)
			: // ステータス進行時に見積履歴を作成する
				await createDealEstimationHeader(deal.id);
		if (isSuccess(result)) {
			await handleUpdateStatus(nextStatusId);
			await dealTodos.mutate();
			if (uncreatedTodoCount !== 0) {
				toast.success(`${uncreatedTodoCount}件のTODOを追加しました。`);
			}
			return;
		}
		toast.error("エラーが発生しました");
	};
	const handleBackwardStatus = async (): Promise<void> => {
		if (prevStatusId === null) return;
		await handleUpdateStatus(prevStatusId);
	};
	const handlePartiallyFinish = async (): Promise<void> => {
		if (!allowPartiallyFinish) return;
		handleUpdateStatus(PARTIALLY_FINISHED);
	};
	return (
		<>
			<Loader />
			<StatusHandleButtons
				allowPartiallyFinish={allowPartiallyFinish}
				backwardDisabled={backwardDisabled}
				canBackward={prevStatusId !== null}
				forwardDisabled={forwardDisabled}
				handleBackwardStatus={handleBackwardStatus}
				handleForwardStatus={handleForwardStatus}
				handlePartiallyFinish={handlePartiallyFinish}
			/>
		</>
	);
};
